import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import {
  Alert,
  Button,
  Paragraph,
  Skeleton,
} from '@digdir/designsystemet-react';
import { ArrowLeftIcon } from '@navikt/aksel-icons';
import type {
  PlanArea,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { useActionPlanningQuery } from '../../hooks/queries/useActionPlanningQuery';
import { useSurveyActions } from '../../hooks/useSurveyActions';
import { useSurveyPaths } from '../../hooks/useSurveyPaths';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { ProcessHero } from './components/ProcessHero';
import { EvaluationStartCard } from './followUp/EvaluationStartCard';
import { hasSafetyRep, planForArea } from './planView';
import { FollowUpStatusCard } from './followUp/FollowUpStatusCard';
import { MeasurementCard } from './followUp/MeasurementCard';
import { MeasuresCard } from './followUp/MeasuresCard';
import { OtherAreasCard } from './followUp/OtherAreasCard';
import { PlanFactsCard } from './followUp/PlanFactsCard';
import { PlansOverviewCard } from './followUp/PlansOverviewCard';
import { ReviewsCard } from './followUp/ReviewsCard';
import { followedUpAreas } from './followUpView';
import { areaName } from './riskView';
import { currentStepIndex } from './surveyProcessView';
import { WorkEnvironmentSurveyPlan } from './WorkEnvironmentSurveyPlan';
import { AREA_PARAM } from './surveyRoutes';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';
import { ReportCard } from './components/ReportCard';
import { ShareCard } from './components/ShareCard';
import { teamPageLink } from './sharingView';

/** Where the plans are changed or new ones made, from the follow-up */
type Planning = { area: string | null } | null;

const introFor = (area: PlanArea | null, count: number, t: TFunction) =>
  area?.plan?.frequency
    ? t('workEnvironmentFollowUp.intro', {
        area: areaName(area, t).toLowerCase(),
        every: t(`workEnvironmentFollowUp.every.${area.plan.frequency}`),
      })
    : t('workEnvironmentFollowUp.introMany', { count });

/**
 * Step 8: the approved plans are carried out, reviewed with the team and changed when needed.
 * With several plans, the page starts with an overview, and each plan is followed up on its own.
 */
export const WorkEnvironmentSurveyFollowUp = ({
  survey,
}: {
  survey: WorkEnvironmentSurvey;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  // The pulse and safety pages belong to the team leader's part of the app
  const { section } = useSurveyPaths();
  const { data, isLoading, isError } = useActionPlanningQuery(
    survey.id,
    questionLanguageFor(i18n.language),
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [params] = useSearchParams();
  // A link to an area still without a plan, as «?omrade=VIOLENCE», opens the plans at that area
  const [planning, setPlanning] = useState<Planning>(() => {
    const area = params.get(AREA_PARAM);
    return area ? { area } : null;
  });

  if (planning) {
    return (
      <WorkEnvironmentSurveyPlan
        survey={survey}
        start={planning.area ? { area: planning.area, edit: true } : undefined}
        onBack={() => setPlanning(null)}
      />
    );
  }

  const areas = data ? followedUpAreas(data.areas) : [];
  const single = areas.length === 1;
  const area = single
    ? areas[0]
    : (areas.find((candidate) => candidate.key === selected) ?? null);
  const plan = area?.plan ? planForArea(area.plan, area.key) : null;
  const waiting = (area ? [area] : areas).some(
    (candidate) => !candidate.plan?.approved,
  );
  const evaluationCard = data?.evaluation && (
    <EvaluationStartCard
      surveyId={survey.id}
      readiness={data.evaluation}
      canStart={
        (data.canStartEvaluation ?? data.canFollowUp) && may('startEvaluation')
      }
      waiting={areas.some((candidate) => !candidate.plan?.approved)}
    />
  );

  return (
    <div data-size="md">
      <ProcessHero
        title={t(
          area || !data
            ? 'workEnvironmentFollowUp.heading'
            : 'workEnvironmentFollowUp.headingMany',
        )}
        surveyTitle={survey.title}
        intro={data ? introFor(area, areas.length, t) : ''}
        tag={{
          label: t(
            waiting
              ? 'workEnvironmentFollowUp.tag.reopened'
              : 'workEnvironmentFollowUp.tag.active',
          ),
          color: waiting ? 'warning' : 'success',
        }}
        process={{
          current: currentStepIndex(survey.status),
          createdAt: survey.createdAt,
          responseDeadline: null,
        }}
      />
      <div className={process.layout}>
        {isLoading && (
          <Skeleton variant="rectangle" width="100%" height={320} />
        )}
        {isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentFollowUp.error')}</Paragraph>
          </Alert>
        )}
        {data && !area && areas.length > 0 && (
          <>
            <div className={classes.mainColumn}>
              {evaluationCard}
              <PlansOverviewCard areas={areas} onOpen={setSelected} />
            </div>
            <div className={process.side}>
              <ReportCard surveyId={survey.id} report="inspection" />
              <OtherAreasCard
                areas={data.areas}
                canChange={data.canChange && may('makePlan')}
                onPlan={() => setPlanning({ area: null })}
              />
            </div>
          </>
        )}
        {data && area && plan && (
          <>
            <div className={classes.mainColumn}>
              {!single && (
                <div>
                  <Button
                    type="button"
                    variant="tertiary"
                    data-size="sm"
                    onClick={() => setSelected(null)}
                  >
                    <ArrowLeftIcon aria-hidden />
                    {t('workEnvironmentFollowUp.back')}
                  </Button>
                </div>
              )}
              {single && evaluationCard}
              <FollowUpStatusCard
                plan={plan}
                hasSafetyRep={hasSafetyRep(data)}
                makesPlans={may('makePlan')}
                onOpenPlan={() => setPlanning({ area: area.key })}
              />
              <MeasuresCard
                surveyId={survey.id}
                plan={plan}
                canFollowUp={data.canFollowUp && may('followUp')}
              />
              <ReviewsCard
                surveyId={survey.id}
                area={area}
                plan={plan}
                canFollowUp={data.canFollowUp && may('followUp')}
              />
            </div>
            <div className={process.side}>
              <ReportCard surveyId={survey.id} report="inspection" />
              <ShareCard
                link={teamPageLink(window.location.origin, survey.uuid)}
                text={t('workEnvironmentSharing.plan')}
                linkLabel={t('workEnvironmentSharing.teamPage')}
                sharing={{
                  surveyId: survey.id,
                  teamId: survey.teamId,
                  kind: 'PLAN',
                }}
              />
              <PlanFactsCard
                surveyId={survey.id}
                area={area}
                plan={plan}
                canChange={data.canChange && may('makePlan')}
                onEdit={() => setPlanning({ area: area.key })}
              />
              {data.measurements &&
                section !== 'safetyRep' &&
                (plan.measurePulse || plan.measureSafety) && (
                  <MeasurementCard
                    plan={plan}
                    measurements={data.measurements}
                  />
                )}
              {single && (
                <OtherAreasCard
                  areas={data.areas}
                  canChange={data.canChange && may('makePlan')}
                  onPlan={() => setPlanning({ area: null })}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
